import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { LOGO_URL } from "@/lib/brand";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Force the "scrolled" treatment on non-home pages so the logo/nav is always readable.
  const compact = scrolled || !onHome;

  const navItems = [
    { id: "suite", label: t.nav.suite },
    { id: "gallery", label: t.nav.gallery },
    { id: "availability", label: t.nav.availability },
    { id: "loano", label: t.nav.loano },
    { id: "reviews", label: t.nav.reviews },
  ];

  const anchorHref = (id) => (onHome ? `#${id}` : `/#${id}`);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        compact
          ? "backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#E0DACE]/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          data-testid="brand-logo"
          to="/"
          className="flex items-center"
          aria-label="AMAC Family Apartments & Suites"
        >
          <img
            src={LOGO_URL}
            alt="AMAC Family Apartments & Suites"
            className={`w-auto transition-all duration-500 ${
              compact ? "h-16 sm:h-20" : "h-14 sm:h-16"
            }`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((n) => (
            <a
              key={n.id}
              data-testid={`nav-${n.id}`}
              href={anchorHref(n.id)}
              className={`text-sm tracking-wide transition-colors ${
                compact
                  ? "text-[#4A5D6B] hover:text-[#1A3B4D]"
                  : "text-white/85 hover:text-white"
              }`}
            >
              {n.label}
            </a>
          ))}
          <Link
            data-testid="nav-about"
            to="/about"
            className={`text-sm tracking-wide transition-colors ${
              compact
                ? "text-[#4A5D6B] hover:text-[#1A3B4D]"
                : "text-white/85 hover:text-white"
            }`}
          >
            {t.nav.about}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <div
            className={`hidden sm:flex items-center text-xs font-medium rounded-full border overflow-hidden ${
              compact
                ? "border-[#E0DACE] text-[#1A3B4D]"
                : "border-white/40 text-white"
            }`}
            data-testid="language-toggle"
          >
            <button
              data-testid="lang-it"
              onClick={() => setLang("it")}
              className={`px-3 py-1.5 tracking-wider transition-colors ${
                lang === "it"
                  ? compact
                    ? "bg-[#1A3B4D] text-[#FAF9F6]"
                    : "bg-white text-[#1A3B4D]"
                  : ""
              }`}
            >
              IT
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 tracking-wider transition-colors ${
                lang === "en"
                  ? compact
                    ? "bg-[#1A3B4D] text-[#FAF9F6]"
                    : "bg-white text-[#1A3B4D]"
                  : ""
              }`}
            >
              EN
            </button>
          </div>

          <a
            data-testid="header-book-btn"
            href={onHome ? "#booking" : "/#booking"}
            className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-[#1A3B4D] text-[#FAF9F6] text-sm tracking-wide hover:bg-[#102632] transition-colors"
          >
            {t.nav.book}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden p-2 ${
              compact ? "text-[#121C26]" : "text-white"
            }`}
          >
            <span className="block w-6 h-px bg-current mb-1.5"></span>
            <span className="block w-6 h-px bg-current mb-1.5"></span>
            <span className="block w-4 h-px bg-current ml-auto"></span>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden backdrop-blur-xl bg-[#FAF9F6]/95 border-t border-[#E0DACE]/60 px-6 py-6"
        >
          <nav className="flex flex-col gap-4">
            {navItems.map((n) => (
              <a
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                href={anchorHref(n.id)}
                onClick={() => setMobileOpen(false)}
                className="text-[#121C26] text-base"
              >
                {n.label}
              </a>
            ))}
            <Link
              to="/about"
              data-testid="mobile-nav-about"
              onClick={() => setMobileOpen(false)}
              className="text-[#121C26] text-base"
            >
              {t.nav.about}
            </Link>
            <a
              href={onHome ? "#booking" : "/#booking"}
              data-testid="mobile-book-btn"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#1A3B4D] text-[#FAF9F6] text-sm tracking-wide"
            >
              {t.nav.book}
            </a>
            <div className="flex gap-2 pt-2">
              <button
                data-testid="m-lang-it"
                onClick={() => setLang("it")}
                className={`flex-1 py-2 rounded-full border ${
                  lang === "it"
                    ? "bg-[#1A3B4D] text-white border-[#1A3B4D]"
                    : "border-[#E0DACE] text-[#1A3B4D]"
                }`}
              >
                IT
              </button>
              <button
                data-testid="m-lang-en"
                onClick={() => setLang("en")}
                className={`flex-1 py-2 rounded-full border ${
                  lang === "en"
                    ? "bg-[#1A3B4D] text-white border-[#1A3B4D]"
                    : "border-[#E0DACE] text-[#1A3B4D]"
                }`}
              >
                EN
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
